/* eslint-disable no-param-reassign */
/**
 * @fileoverview Hook Actions Layer para lógica de negocio de ChatComponentMC.
 * @module useChatComponentActions
 * @category Hook - Actions Layer
 * @since 1.0.0
 * @version 1.0.0
 */
import { useCallback, useEffect, useRef } from 'react';
import useCompleteMessageActions from "./useCompleteMessageActions";
import { sharingInformationService } from '../../../../services/sharingInformationService';
import { isTikTokPlatform, TIKTOK_BLOCKED_TYPES } from "../constants/platformMessageRestrictions";
import COMMENT_THREAD_PLATFORMS from "../constants/commentThreadPlatforms";
import { buildVoiceNoteFile } from '../../../../util/audioUtils';
import { isInboxChatObjectId } from '../../../../services/inbox/resolveOutboundRecipient';

/**
 * Specialized hook to manage the business logic of sending messages and creating order messages in ChatComponentMC.
 *
 * @param {Object} params - Hook parameters
 * @param {Object} params.conversation - Current conversation
 * @param {function} params.setOpenAttachMenu - Setter for attach menu (UI Layer)
 * @param {Object} params.methods - Form methods (react-hook-form)
 * @param {React.RefObject} params.textareaRef - Reference to the textarea
 *
 * @returns {Object} Business handlers
 * @property {function} handleSubmit - Handler to send messages
 * @property {function} createOrderMessage - Handler to create order message
 */
function useChatComponentActions({
  conversation,
  setOpenAttachMenu,
  textareaRef,
  productActions,
  fastAnswerActions,
  replyMessage,
  clearReplyMessage,
  handleIncomingText,
  flushConsolidatedMessage
}) {
  const {
    sendAndAddMessageWithFiles,
    sendAndAddMessage,
    sendAndAddMessageOrder
  } = useCompleteMessageActions();

  // Use refs to avoid recreating functions when these values change
  const productActionsRef = useRef(productActions);
  const fastAnswerActionsRef = useRef(fastAnswerActions);
  const conversationRef = useRef(conversation);
  const replyMessageRef = useRef(replyMessage);
  const clearReplyMessageRef = useRef(clearReplyMessage);
  const handleIncomingTextRef = useRef(handleIncomingText);
  const flushConsolidatedMessageRef = useRef(flushConsolidatedMessage);

  // Update the refs when these values change
  useEffect(() => {
    productActionsRef.current = productActions;
  }, [productActions]);

  useEffect(() => {
    fastAnswerActionsRef.current = fastAnswerActions;
  }, [fastAnswerActions]);

  useEffect(() => {
    conversationRef.current = conversation;
  }, [conversation]);

  useEffect(() => {
    replyMessageRef.current = replyMessage;
  }, [replyMessage]);

  useEffect(() => {
    clearReplyMessageRef.current = clearReplyMessage;
  }, [clearReplyMessage]);

  useEffect(() => {
    handleIncomingTextRef.current = handleIncomingText;
  }, [handleIncomingText]);

  useEffect(() => {
    flushConsolidatedMessageRef.current = flushConsolidatedMessage;
  }, [flushConsolidatedMessage]);

  /**
   * Handler to send messages (text, file, image, video).
   * @memberof useChatComponentActions
   */
  const handleSubmit = useCallback((type, data, targetCustomerId) => {
    const conv = conversationRef.current;
    const rawCustomerId = conv?.customer_id || conv?.customerId || conv?.attributes?.customer_id;
    const rawChatId = conv?.id || conv?.chat_id || conv?.attributes?.id;
    const platform = conv?.platform || conv?.attributes?.platform || conv?.type;

    let customerId;
    let chatId;

    if (targetCustomerId) {
      if (isInboxChatObjectId(targetCustomerId)) {
        chatId = String(targetCustomerId);
        customerId = rawCustomerId || targetCustomerId;
      } else {
        customerId = targetCustomerId;
        chatId = rawChatId;
      }
    } else {
      customerId = rawCustomerId || rawChatId;
      chatId = rawChatId;
    }

    // En hilos de comentarios el backend resuelve el hilo por `customer_id`,
    // que debe ser el id del CustomerFbPost (chat_id) y no el id del Customer.
    if (COMMENT_THREAD_PLATFORMS.has(platform) && chatId) {
      customerId = chatId;
    }

    if (isTikTokPlatform(platform) && TIKTOK_BLOCKED_TYPES.has(type)) {
      console.warn(`TikTok no admite mensajes de tipo: ${type}`);
      return;
    }

    if (type !== 'text') {
      flushConsolidatedMessageRef.current?.();
    }

    const messageData = {
      type,
      template: false,
      platform,
      customer_id: customerId,
      chat_id: chatId
    };

    if (replyMessageRef.current) {
      data.reply_to = replyMessageRef.current.id;
    }

    if (["file", "image", "video"].includes(type)) {
      messageData.file_data = data;
      sendAndAddMessageWithFiles(messageData);
    } else {
      const otherMessageData = {
        ...messageData,
        ...data,
        isComment: conv?.isComment || false
      };
      sendAndAddMessage(otherMessageData);
    }
    setOpenAttachMenu(false);
    clearReplyMessageRef.current?.();
  }, [
    sendAndAddMessageWithFiles,
    sendAndAddMessage,
    setOpenAttachMenu,
    replyMessageRef,
    clearReplyMessageRef
  ]);

  /**
   * Handler to create order message.
   * @memberof useChatComponentActions
   */
  const createOrderMessage = useCallback((orderMessage) => {
    const conv = conversationRef.current;
    flushConsolidatedMessageRef.current?.();
    sendAndAddMessageOrder({
      customer_id: conv?.id,
      isComment: conv?.isComment || false,
      orderData: orderMessage,
      platform: conv?.platform
    });
  }, [sendAndAddMessageOrder]);

  /**
   * Effect to subscribe to order creation events.
   */
  useEffect(() => {
    const subscription$ = sharingInformationService.getSubject();

    const observable = subscription$.subscribe((data) => {
      if (data.orderMessage) {
        createOrderMessage(data.orderMessage);
      }
    });

    return () => {
      observable.unsubscribe();
    };
  }, [createOrderMessage]);

  /**
   * Handler to submit and reset text message.
   * @memberof useChatComponentActions
   */
  const handleSendMessage = useCallback(({ type, blob } = {}) => {
    const message = textareaRef.current?.value || '';
    const conv = conversationRef.current;

    if (type === 'audio' && isTikTokPlatform(conv?.platform)) {
      console.warn('TikTok no admite mensajes de audio');
      return;
    }

    if (type && blob) {
      flushConsolidatedMessageRef.current?.();
      // Named File with a real MIME — unnamed Blobs upload as "blob" and fail on Safari.
      const file = buildVoiceNoteFile(blob);

      const data = {
        type,
        template: false,
        platform: conv?.platform,
        customer_id: conv?.id,
        file_data: file,
        file_name: file.name
      };

      if (replyMessageRef.current) {
        data.reply_to = replyMessageRef.current.id;
      }

      sendAndAddMessageWithFiles(data);
      clearReplyMessageRef.current?.();
      return;
    }

    const trySendProduct = productActionsRef.current?.handleSendProductFromInput?.(message);
    if (trySendProduct) {
      flushConsolidatedMessageRef.current?.();
      // Clear textarea directly
      if (textareaRef.current) {
        textareaRef.current.value = '';
        textareaRef.current.dispatchEvent(new Event('input', { bubbles: true }));
      }
      return;
    }

    // Try to send as fast answer
    const fastAnswerSent = fastAnswerActionsRef.current?.handleSendFastAnswerFromInput?.(message);
    if (fastAnswerSent) {
      flushConsolidatedMessageRef.current?.();
      // Clear textarea directly
      if (textareaRef.current) {
        textareaRef.current.value = '';
        textareaRef.current.dispatchEvent(new Event('input', { bubbles: true }));
      }
      return;
    }

    // If there is no fast answer, send as normal message
    if (message.trim()) {
      const isConsolidated = handleIncomingTextRef.current?.(message);
      if (isConsolidated) {
        if (textareaRef.current) {
          textareaRef.current.value = '';
          textareaRef.current.dispatchEvent(new Event('input', { bubbles: true }));
        }
        return;
      }

      handleSubmit('text', { message });
      // Clear textarea directly
      if (textareaRef.current) {
        textareaRef.current.value = '';
        textareaRef.current.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
  }, [textareaRef, handleSubmit, replyMessageRef, clearReplyMessageRef, sendAndAddMessageWithFiles]);

  /**
   * Inserts an emoji at the cursor position of the chat textarea.
   * @function insertEmoji
   * @memberof useChatComponentActions
   * @param {Object} emojiData - Emoji-mart emoji object
   */
  const insertEmoji = useCallback((emojiData) => {
    if (!textareaRef?.current) return;
    const emoji = emojiData.native;
    const textarea = textareaRef.current;
    const message = textarea.value || '';
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;

    // Insert the emoji at the cursor position
    const newMessage = message.slice(0, start) + emoji + message.slice(end);
    textarea.value = newMessage;

    // Trigger event to let React Hook Form detect the change
    textarea.dispatchEvent(new Event('input', { bubbles: true }));

    // Restore the cursor after updating the value
    setTimeout(() => {
      textarea?.focus();
      const cursorPos = start + emoji.length;
      textarea?.setSelectionRange(cursorPos, cursorPos);
    }, 0);
  }, [textareaRef]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  }, [
    handleSendMessage
  ]);

  return {
    handleKeyDown,
    handleSubmit,
    handleSendMessage,
    insertEmoji
  };
}

export default useChatComponentActions;
